// Tenant preferences — the tenant-owned half of the estimation masters (EST module).
// Backend: api-service app/api/v1/endpoints/tenant_preferences.py, app/domain/tenant_preferences/
//
// One generic mechanism serves three "kinds" (service-rates, materials, products), matching the
// backend's own REGISTRY-driven router — see root CLAUDE.md's "one generic mechanism, not N
// bespoke routers" rule. Upsert is by DERIVED natural key (service_code[+effective_from] for
// service-rates, material_code for materials, material_code+product_code for products) — there is
// no separate create/update call.
//
// Every list here is server-searched and paginated (`?search=&skip=&limit=`) rather than loaded
// whole — both this domain's own tables and the platform material/manufacturer/service pickers
// are meant to keep working as a tenant's catalogue grows into the hundreds or thousands.
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}
interface ApiListResponse<T> extends ApiResponse<T[]> {
  total: number;
}

export interface SearchQuery {
  search?: string;
  skip?: number;
  limit?: number;
  [key: string]: string | number | boolean | undefined;
}

export interface Page<T> {
  items: T[];
  total: number;
}

function prune(
  params: Record<string, string | number | boolean | undefined>
): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  ) as Record<string, string | number | boolean>;
}

export type SelectionMethod =
  | 'PREFERRED_SUPPLIER'
  | 'LOWEST_COST'
  | 'PREFERRED_MANUFACTURER'
  | 'SPECIFIC_PRODUCT'
  | 'MANUAL';

// ── service_rate_master (tenant's own labour rates, effective-dated) ──────────
export interface ServiceRate {
  service_rate_code: string;
  tenant_code?: string | null;
  service_code: string;
  base_rate: number;
  rate_uom?: string | null;
  currency: string;
  effective_from?: string | null;
  effective_to?: string | null;
  active: boolean;
}

export type ServiceRateUpsert = Pick<ServiceRate, 'service_code' | 'base_rate'> &
  Partial<Pick<ServiceRate, 'rate_uom' | 'currency' | 'effective_from' | 'effective_to' | 'active'>>;

// One row per service in the platform catalogue, carrying whichever rate actually applies —
// this tenant's own if set, else the platform default. Editing always forks a tenant row (via
// upsertServiceRate below), which from then on shadows the platform default for this tenant only.
export interface EffectiveServiceRate {
  service_code: string;
  service_name: string;
  default_uom: string;
  base_rate: number | null;
  rate_uom: string | null;
  currency: string;
  rate_source: 'TENANT_RATE' | 'PLATFORM_RATE' | 'NONE';
  service_rate_code: string | null;
}

// ── tenant_material_preference (preferred supplier / manufacturer per material) ──
export interface MaterialPreference {
  tenant_material_preference_code: string;
  tenant_code?: string | null;
  material_code: string;
  preferred_manufacturer_code?: string | null;
  preferred_supplier_code?: string | null;
  selection_method: SelectionMethod;
  allow_user_change_product: boolean;
  allow_user_change_supplier: boolean;
  allow_price_override: boolean;
  active: boolean;
}

export type MaterialPreferenceUpsert = Pick<MaterialPreference, 'material_code'> &
  Partial<
    Pick<
      MaterialPreference,
      | 'preferred_manufacturer_code'
      | 'preferred_supplier_code'
      | 'selection_method'
      | 'allow_user_change_product'
      | 'allow_user_change_supplier'
      | 'allow_price_override'
      | 'active'
    >
  >;

// ── Platform reference-data pickers (app/api/v1/endpoints/estimation_masters.py) ──
export interface MaterialOption {
  material_code: string;
  material_name: string;
  base_uom?: string | null;
}
export interface ManufacturerOption {
  manufacturer_code: string;
  manufacturer_name: string;
}
export interface ServiceOption {
  service_code: string;
  service_name: string;
  default_uom: string;
}

// ── tenant_material (a tenant's OWN material, not the platform catalogue's) ──
export interface TenantMaterial {
  material_code: string;
  tenant_code?: string | null;
  material_name: string;
  category_code?: string | null;
  description?: string | null;
  base_uom?: string | null;
  active: boolean;
}

export type TenantMaterialUpsert = Pick<TenantMaterial, 'material_name'> &
  Partial<Pick<TenantMaterial, 'category_code' | 'description' | 'base_uom' | 'active'>>;

export const tenantPreferencesApi = createApi({
  reducerPath: 'tenantPreferencesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ServiceRates', 'MaterialPreferences', 'CustomMaterials'],
  endpoints: (builder) => ({
    listServiceRates: builder.query<Page<ServiceRate>, SearchQuery | void>({
      query: (params) => ({ url: '/api/v1/tenant-preferences/service-rates', params: prune(params ?? {}) }),
      transformResponse: (res: ApiListResponse<ServiceRate>) => ({ items: res.data, total: res.total }),
      providesTags: ['ServiceRates'],
    }),
    // Every service, platform default filled in where the tenant has no rate of its own.
    listEffectiveServiceRates: builder.query<Page<EffectiveServiceRate>, SearchQuery | void>({
      query: (params) => ({
        url: '/api/v1/tenant-preferences/service-rates',
        params: prune({ effective: true, ...params }),
      }),
      transformResponse: (res: ApiListResponse<EffectiveServiceRate>) => ({ items: res.data, total: res.total }),
      providesTags: ['ServiceRates'],
    }),
    upsertServiceRate: builder.mutation<ServiceRate, ServiceRateUpsert>({
      query: (body) => ({ url: '/api/v1/tenant-preferences/service-rates', method: 'PUT', body }),
      transformResponse: (res: ApiResponse<ServiceRate>) => res.data,
      invalidatesTags: ['ServiceRates'],
    }),
    deleteServiceRate: builder.mutation<void, string>({
      query: (code) => ({ url: `/api/v1/tenant-preferences/service-rates/${code}`, method: 'DELETE' }),
      invalidatesTags: ['ServiceRates'],
    }),

    listMaterialPreferences: builder.query<Page<MaterialPreference>, SearchQuery | void>({
      query: (params) => ({ url: '/api/v1/tenant-preferences/materials', params: prune(params ?? {}) }),
      transformResponse: (res: ApiListResponse<MaterialPreference>) => ({ items: res.data, total: res.total }),
      providesTags: ['MaterialPreferences'],
    }),
    upsertMaterialPreference: builder.mutation<MaterialPreference, MaterialPreferenceUpsert>({
      query: (body) => ({ url: '/api/v1/tenant-preferences/materials', method: 'PUT', body }),
      transformResponse: (res: ApiResponse<MaterialPreference>) => res.data,
      invalidatesTags: ['MaterialPreferences'],
    }),
    deleteMaterialPreference: builder.mutation<void, string>({
      query: (code) => ({ url: `/api/v1/tenant-preferences/materials/${code}`, method: 'DELETE' }),
      invalidatesTags: ['MaterialPreferences'],
    }),

    // Platform reference-data pickers — server-searched/paginated so a catalogue with hundreds or
    // thousands of materials/manufacturers/services never loads whole into the browser.
    searchMaterialOptions: builder.query<Page<MaterialOption>, SearchQuery | void>({
      query: (params) => ({ url: '/api/v1/estimation-masters/materials', params: prune({ limit: 25, ...params }) }),
      transformResponse: (res: ApiListResponse<MaterialOption>) => ({ items: res.data, total: res.total }),
    }),
    searchManufacturerOptions: builder.query<Page<ManufacturerOption>, SearchQuery | void>({
      query: (params) => ({ url: '/api/v1/estimation-masters/manufacturers', params: prune({ limit: 25, ...params }) }),
      transformResponse: (res: ApiListResponse<ManufacturerOption>) => ({ items: res.data, total: res.total }),
    }),
    searchServiceOptions: builder.query<Page<ServiceOption>, SearchQuery | void>({
      query: (params) => ({ url: '/api/v1/estimation-masters/services', params: prune({ limit: 25, ...params }) }),
      transformResponse: (res: ApiListResponse<ServiceOption>) => ({ items: res.data, total: res.total }),
    }),

    // A tenant's own materials — for a material the platform catalogue doesn't carry. Usable by
    // preferences/pricing like a platform material, but not wired into any WBS template.
    listCustomMaterials: builder.query<Page<TenantMaterial>, SearchQuery | void>({
      query: (params) => ({ url: '/api/v1/tenant-preferences/custom-materials', params: prune(params ?? {}) }),
      transformResponse: (res: ApiListResponse<TenantMaterial>) => ({ items: res.data, total: res.total }),
      providesTags: ['CustomMaterials'],
    }),
    upsertCustomMaterial: builder.mutation<TenantMaterial, TenantMaterialUpsert>({
      query: (body) => ({ url: '/api/v1/tenant-preferences/custom-materials', method: 'PUT', body }),
      transformResponse: (res: ApiResponse<TenantMaterial>) => res.data,
      invalidatesTags: ['CustomMaterials'],
    }),
    deleteCustomMaterial: builder.mutation<void, string>({
      query: (code) => ({ url: `/api/v1/tenant-preferences/custom-materials/${code}`, method: 'DELETE' }),
      invalidatesTags: ['CustomMaterials', 'MaterialPreferences'],
    }),
  }),
});

export const {
  useListServiceRatesQuery,
  useListEffectiveServiceRatesQuery,
  useUpsertServiceRateMutation,
  useDeleteServiceRateMutation,
  useListMaterialPreferencesQuery,
  useUpsertMaterialPreferenceMutation,
  useDeleteMaterialPreferenceMutation,
  useSearchMaterialOptionsQuery,
  useSearchManufacturerOptionsQuery,
  useSearchServiceOptionsQuery,
  useListCustomMaterialsQuery,
  useUpsertCustomMaterialMutation,
  useDeleteCustomMaterialMutation,
} = tenantPreferencesApi;
